import { TrailerFieldNames } from '@contracts/order/fields/TrailerInformationFields';
import { ContactCard } from '@src/authentication/ContactCard';
import { useAuthentication } from '@src/authentication/useAuthentication';
import type { ClientForSelector } from '@src/clients/ClientSelectOption';
import { useClientsSummary } from '@src/clients/hooks/useClientsSummary';
import { isClientUser } from '@src/clientUser/useClientUser';
import { InvisibleButtonLink } from '@src/common/components/button/InvisibleButtonLink';
import { Card } from '@src/common/components/Card';
import { HorizontalDivider } from '@src/common/components/HorizontalDivider';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { Timestamp } from '@src/common/time/Timestamp';
import { UserDropdown } from '@src/manageUsers/components/UserDropdown';
import type { User } from '@src/manageUsers/domain/User';
import { useUsers } from '@src/manageUsers/hooks/useUsers';
import { ClientSelectionSection } from '@src/orders/ClientSelectionSection';
import { BillToDropdown } from '@src/orders/components/billTo/BillToDropdown';
import { DetailsItem } from '@src/orders/components/DetailsItem';
import { OrderActionsMenu } from '@src/orders/components/orderActionsMenu/OrderActionsMenu';
import { OrderStatusBadge } from '@src/orders/components/OrderStatusBadge';
import { UpdatableDetailsItem } from '@src/orders/components/UpdatableDetailsItem';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { OrderAlreadyClaimedException } from '@src/orders/exceptions/OrderAlreadyClaimedException';
import { useClaimOrder } from '@src/orders/hooks/useClaimOrder';
import { useUnclaimOrder } from '@src/orders/hooks/useUnclaimOrder';
import { useUpdateOrderClient } from '@src/orders/hooks/useUpdateOrderClient';
import { useUpdateBillTo } from '@src/orders/review/hooks/useUpdateBillTo';
import { PONumberField } from '@src/permitField/PONumberField';
import {
  TractorLicensePlateNumberField,
  TractorUnitNumberField,
} from '@src/permitField/TractorInformationFields';
import { TrailerLicensePlateField } from '@src/permitField/TrailerInformationFields';
import { ClientsNavigation } from '@src/routing/ClientsNavigation';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type OrderSummaryProps = {
  order: OrderListing;
  onAssign: () => void;
  onStatusChange: () => void;
  onDeleteSuccess: () => void;
};

export const OrderSummary = ({
  order,
  onAssign,
  onStatusChange,
  onDeleteSuccess,
}: OrderSummaryProps) => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToast();
  const { user: currentUser } = useAuthentication();
  const { claimOrder } = useClaimOrder({
    orderId: order.id,
    onSuccess: () => {
      onAssign();
      showSuccessToast(t('assignUser.success'));
    },
    onError: (error: Error) => {
      if (error instanceof OrderAlreadyClaimedException) {
        showErrorToast(t('assignUser.claimed'));
      } else {
        showErrorToast(t('assignUser.error'));
      }
    },
  });

  const { unclaimOrder } = useUnclaimOrder({
    orderId: order.id,
    onSuccess: () => {
      onAssign();
      showSuccessToast(t('unassignUser.success'));
    },
    onError: () => {
      showErrorToast(t('unassignUser.error'));
    },
  });

  const assignUser = async (user: User | null) => {
    if (user) {
      claimOrder(user.id);
    } else if (order.claimedBy) {
      unclaimOrder();
    }
  };

  const { userList } = useUsers();
  const usersById: Record<string, User> = userList.users.reduce(
    (accumulator, user) => {
      return { ...accumulator, [user.id]: user };
    },
    {},
  );

  const orderAssignee = usersById[order.claimedBy ?? ''] ?? null;

  return (
    <Card aria-label={`order-${order.id}-details`} className={orderSummaryCard}>
      <Stack gap={theme.spacing['6']}>
        <Stack direction="row" justifyContent="space-between">
          <OrderStatusBadge status={order.status} />
          <Stack
            direction="row"
            display="flex"
            alignItems="center"
            gap={2}
            flex={1}
            justifyContent="flex-end"
            maxWidth="50%"
          >
            {isClientUser(currentUser) ? (
              <ContactCard user={orderAssignee} />
            ) : (
              <>
                <Typography style={{ fontWeight: 600 }}>
                  {t('assignTo')}:&nbsp;
                </Typography>
                <UserDropdown
                  selectedUser={order.claimedBy}
                  placeholder={t('unassigned')}
                  disabled={order.isCompleted()}
                  getOptionDisabled={(user) =>
                    !!order.claimedBy &&
                    !order.canBeEditedBy(currentUser?.id) &&
                    (user as User).id !== order.claimedBy
                  }
                  onUserChange={assignUser}
                />
              </>
            )}
            <OrderActionsMenu
              order={order}
              onStatusChange={onStatusChange}
              onDeleteSuccess={onDeleteSuccess}
            />
          </Stack>
        </Stack>

        <Stack direction="column" gap={theme.spacing['3']}>
          <ClientInformation order={order} onClientUpdate={onStatusChange} />
          <OrderIdentifiers order={order} />
        </Stack>

        <HorizontalDivider />

        <Stack direction="row" gap={theme.spacing['3']}>
          <TractorInformation order={order} />
          <TrailerInformation order={order} />
          <OrderCreationInformation order={order} />
        </Stack>
      </Stack>
    </Card>
  );
};

const orderSummaryCard = css({
  backgroundColor: theme.palette.surface.surface,
  padding: theme.spacing['6'],
  marginTop: '56px',
});

const fullWidth = css({
  flex: 1,
});

const forceFullWidth = css({
  width: '100%',
  '& > *': {
    width: '100%',
  },
});

const getLastUpdated = (fieldName: string, order: OrderListing) => {
  const lastUpdatedDate = order.getLastUpdatedFor(fieldName);
  return lastUpdatedDate
    ? new Timestamp(lastUpdatedDate).formatUTCDate()
    : undefined;
};

const LASERFICHE_CREATED_BY = '__LASERFICHE__';
const getCreatedBy = (order: OrderListing) => {
  if (order.details.createdBy === LASERFICHE_CREATED_BY) {
    return t('orderSummary.createdBy.laserficheDisplay');
  }
  return order.details.createdBy;
};

const OrderCreationInformation = ({ order }: { order: OrderListing }) => {
  const { t } = useTranslation();

  return (
    <Stack
      className={fullWidth}
      gap={theme.spacing['3']}
      direction="column"
      alignItems="left"
      justifyContent="flex-start"
    >
      <DetailsItem
        itemName={t('orderSummary.effective')}
        value={order.details.effectiveDate.formatUTCDate()}
        valueEmphasis
      />
      <DetailsItem
        itemName={t('orderSummary.submitted')}
        value={order.details.submittedDate.formatDate()}
      />
      <DetailsItem
        itemName={t('orderSummary.createdBy.label')}
        value={getCreatedBy(order)}
      />
    </Stack>
  );
};

const TrailerInformation = ({ order }: { order: OrderListing }) => {
  const { t } = useTranslation();

  return (
    <Stack
      className={fullWidth}
      gap={theme.spacing['3']}
      direction="column"
      alignItems="left"
      justifyContent="flex-start"
    >
      <Typography variant="subtitle2">{`${t('orderSummary.trailerColumnTitle')}`}</Typography>
      <UpdatableDetailsItem
        itemName={t('orderSummary.leadingTrailerUnitNumber')}
        value={order.details.leadTrailerUnit?.toString()}
        lastUpdated={getLastUpdated(TrailerFieldNames.TrailerUnitNumber, order)}
      />
      <UpdatableDetailsItem
        itemName={t('orderSummary.trailerLicensePlate')}
        value={order.details.leadTrailerLicensePlate?.toString()}
        lastUpdated={getLastUpdated(TrailerLicensePlateField.fieldName, order)}
      />
    </Stack>
  );
};

const TractorInformation = ({ order }: { order: OrderListing }) => {
  const { t } = useTranslation();

  return (
    <Stack
      className={fullWidth}
      gap={theme.spacing['3']}
      direction="column"
      alignItems="left"
      justifyContent="flex-start"
    >
      <Typography variant="subtitle2">{`${t('orderSummary.tractorColumnTitle')}`}</Typography>
      <UpdatableDetailsItem
        itemName={t('orderSummary.tractorUnitNumber')}
        value={order.details.tractorUnit?.toString()}
        lastUpdated={getLastUpdated(TractorUnitNumberField.fieldName, order)}
      />
      <UpdatableDetailsItem
        itemName={t('orderSummary.tractorLicensePlate')}
        value={order.details.tractorLicensePlate?.toString()}
        lastUpdated={getLastUpdated(
          TractorLicensePlateNumberField.fieldName,
          order,
        )}
      />
    </Stack>
  );
};

const OrderIdentifiers = ({ order }: { order: OrderListing }) => {
  const { t } = useTranslation();
  const { updateBillingInformation, canUpdateBillTo } = useUpdateBillTo(order);

  return (
    <Stack
      direction="row"
      width="100%"
      display="flex"
      alignItems="flex-start"
      gap={theme.spacing['3']}
      justifyContent="flex-start"
    >
      <Stack
        flex={1}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Typography variant="subtitle2">{`${t('order')} ${order.displayId}`}</Typography>
      </Stack>
      <Stack
        flex={1}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <UpdatableDetailsItem
          itemName={t('orderSummary.purchaseOrder')}
          value={order.details.PONumber}
          lastUpdated={getLastUpdated(PONumberField.fieldName, order)}
        />
      </Stack>
      <Stack
        flex={1}
        direction="column"
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <div className={forceFullWidth}>
          <BillToDropdown
            clientId={order.billToClientId}
            label={t('fields.billTo')}
            disabled={!canUpdateBillTo}
            onChange={(client) =>
              updateBillingInformation({ billTo: client.id })
            }
          ></BillToDropdown>
        </div>
      </Stack>
    </Stack>
  );
};

const ClientInformation = ({
  order,
  onClientUpdate,
}: {
  order: OrderListing;
  onClientUpdate: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToast();
  const canChangeSelectedClient = !!order.client.isTemporary;
  const { clientList, isLoading: loadingClients } = useClientsSummary({
    enabled: canChangeSelectedClient,
  });

  const { updateOrderClient } = useUpdateOrderClient({
    orderId: order.id,
    onSuccess: () => {
      showSuccessToast(t('changeClient.success'));
      onClientUpdate();
    },
    onError: () => {
      showErrorToast(t('changeClient.error'));
    },
  });

  const goToAgencyPage = (agencyId: string) => {
    navigate(ClientsNavigation.get.url(agencyId));
  };

  const handleClientChange = (client: ClientForSelector | null) => {
    if (client && client.id !== order.client.id) {
      updateOrderClient(client.id);
    }
  };

  return (
    <Stack
      gap={theme.spacing['1']}
      style={{ marginBottom: theme.spacing['6'] }}
    >
      {(order.client.responsibleAgency || order.client.isAgency) && (
        <StatusBadge
          size="medium"
          text={
            order.client.responsibleAgency ? t('agencyClient') : t('agency')
          }
          color={colors.neutral[0]}
          borderColor={colors.neutral[0]}
          backgroundColor={colors.neutral[100]}
          radiusFull
        />
      )}
      <Stack
        direction="column"
        justifyContent="space-between"
        gap={theme.spacing['1']}
      >
        <Stack style={{ width: '50%' }} gap={theme.spacing['3']}>
          {order.client.doingBusinessAs ? (
            <Typography variant="h5">{order.client.doingBusinessAs}</Typography>
          ) : (
            order.client.isTemporary && (
              <Typography variant="h5">{order.client.name}</Typography>
            )
          )}
          <ClientSelectionSection
            activeClients={clientList.toSelector()}
            isLoading={loadingClients}
            defaultClient={order.client}
            readonly={!canChangeSelectedClient}
            onClientChange={handleClientChange}
            showTitle={false}
          />
        </Stack>
        {order.client.responsibleAgency && (
          <DetailsItem
            itemName={t('orderSummary.managedBy')}
            value={
              <InvisibleButtonLink
                style={{
                  width: 'fit-content',
                  marginLeft: 'auto',
                  backgroundColor: 'transparent',
                }}
                onClick={() =>
                  goToAgencyPage(order.client.responsibleAgency!.id)
                }
              >
                {order.client.responsibleAgency.name}
              </InvisibleButtonLink>
            }
          />
        )}
      </Stack>
    </Stack>
  );
};
